import React, { useRef, useState, useEffect, useMemo } from 'react';
import * as faceapi from '@vladmandic/face-api';
import { 
  Camera, X, RefreshCw, MapPin, CheckCircle2, AlertTriangle, 
  Info, Search, Sparkles, SwitchCamera, Clock, Check, Building2, 
  Factory, Layers, Car, Navigation, DoorOpen, HelpCircle, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { loadFaceApiModels, areFaceApiModelsLoaded, isFaceDetectorLoaded } from '@/utils/faceApiLoader';

// Daftar Area dengan Simbol Bersih (Tanpa Emoji)
export const ATTENDANCE_AREAS = [
  { id: 'Lahan Parkir', label: 'Lahan Parkir', badge: 'Parkir Gedung', Icon: Car },
  { id: 'Office', label: 'Office', badge: 'Kantor Manajemen', Icon: Building2 },
  { id: 'Pabrik Lantai 1', label: 'Pabrik Lantai 1', badge: 'Produksi Primer & Lab', Icon: Factory },
  { id: 'Pabrik Lantai 2', label: 'Pabrik Lantai 2', badge: 'Sekunder & Packing', Icon: Layers },
  { id: 'Depan Gerbang', label: 'Depan Gerbang', badge: 'Gerbang / Pos Security', Icon: DoorOpen },
  { id: 'Dinas Luar', label: 'Dinas Luar', badge: 'Meeting / Ekspedisi', Icon: Navigation },
  { id: 'Lainnya', label: 'Lainnya', badge: 'Tentukan Sendiri', Icon: HelpCircle },
];

const LATE_REASON_PRESETS = [
  'Macet di perjalanan',
  'Kendaraan bermasalah / mogok',
  'Hujan lebat / cuaca buruk',
  'Keperluan mendesak',
  'Antre di gerbang masuk'
];

export default function FaceScanModal({ isOpen, onClose, onVerified, referenceDescriptorStr, isLate }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [isModelLoaded, setIsModelLoaded] = useState(() => areFaceApiModelsLoaded());
  const [status, setStatus] = useState(() => areFaceApiModelsLoaded() ? 'Model AI siap. Posisikan wajah di kamera.' : 'Menyiapkan AI Wajah...');
  const [isDetecting, setIsDetecting] = useState(false);
  const [faceDetectedLive, setFaceDetectedLive] = useState(false);

  // Freeze Snapshot & Shutter Flash States
  const [frozenPhoto, setFrozenPhoto] = useState(null);
  const [isFlashing, setIsFlashing] = useState(false);

  // Efek suara shutter kamera sintetis (Web Audio API)
  const playShutterSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const audioCtx = new AudioCtx();
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(800, audioCtx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(100, audioCtx.currentTime + 0.08);

      gain.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.08);

      osc.connect(gain);
      gain.connect(audioCtx.destination);

      osc.start();
      osc.stop(audioCtx.currentTime + 0.09);
    } catch {
      // Abaikan jika browser membatasi autoplay audio
    }
  };

  // Area Selection States
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [customOtherArea, setCustomOtherArea] = useState('');
  const [searchAreaQuery, setSearchAreaQuery] = useState('');
  const [lastSavedArea, setLastSavedArea] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }
  }, [isDropdownOpen]);

  // Late Reason State
  const [lateReason, setLateReason] = useState('');

  // Camera Settings
  const [facingMode, setFacingMode] = useState('user'); // 'user' (depan) atau 'environment' (belakang)
  const [currentTime, setCurrentTime] = useState('');

  // Jam digital realtime
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeStr = now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      const dateStr = now.toLocaleDateString('id-ID', { weekday: 'short', day: 'numeric', month: 'short' });
      setCurrentTime(`${dateStr} • ${timeStr} WIB`);
    };
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  // Baca area terakhir saat modal dibuka
  useEffect(() => {
    if (isOpen) {
      try {
        const last = localStorage.getItem('satala_last_attendance_area');
        if (last) {
          setLastSavedArea(last);
          // Jika cocok dengan salah satu preset, auto-select agar cepat
          const matched = ATTENDANCE_AREAS.find(a => a.id === last || last.startsWith(a.id));
          if (matched) {
            setSelectedAreaId(matched.id);
            if (matched.id === 'Lainnya' && last.includes(': ')) {
              setCustomOtherArea(last.split(': ')[1] || '');
            }
          }
        }
      } catch (e) {
        // ignore
      }
    }
  }, [isOpen]);

  // Siklus kamera & model AI
  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      setSelectedAreaId('');
      setCustomOtherArea('');
      setSearchAreaQuery('');
      setLateReason('');
      setIsDetecting(false);
      setFaceDetectedLive(false);
      setIsDropdownOpen(false);
      setFrozenPhoto(null);
      setIsFlashing(false);
      return;
    }

    startCamera(facingMode);

    loadFaceApiModels().then((success) => {
      if (success) {
        setIsModelLoaded(true);
        setStatus(prev => prev.includes('kamera') ? prev : 'Model AI siap. Posisikan wajah di kamera.');
      } else {
        setStatus('Kamera aktif (Mode Standar).');
      }
    });

    return () => {
      stopCamera();
    };
  }, [isOpen, facingMode]);

  // Status message update
  useEffect(() => {
    if (isOpen) {
      if (!selectedAreaId) {
        setStatus('Pilih area lokasi Anda terlebih dahulu.');
      } else if (selectedAreaId === 'Lainnya' && !customOtherArea.trim()) {
        setStatus('Ketikkan nama lokasi spesifik Anda.');
      } else if (isLate && !lateReason.trim()) {
        setStatus('Harap pilih atau tuliskan alasan keterlambatan.');
      } else {
        setStatus('Posisikan wajah Anda di dalam lingkaran oval.');
      }
    }
  }, [isOpen, selectedAreaId, customOtherArea, isLate, lateReason]);

  const startCamera = async (mode) => {
    stopCamera();
    try {
      const constraints = {
        video: { 
          facingMode: mode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
          aspectRatio: { ideal: 1.333333 }
        }
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      streamRef.current = stream;
      setStatus('Kamera aktif. Pilih area dan posisikan wajah.');
    } catch (err) {
      console.error("Camera error:", err);
      // Fallback tanpa constraint khusus
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        streamRef.current = stream;
      } catch (err2) {
        setStatus('Gagal membuka kamera. Pastikan izin kamera telah diberikan.');
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const toggleFlipCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const createSnapshotCanvas = (video) => {
    const canvas = document.createElement('canvas');
    const maxDim = 640;
    let w = video.videoWidth || 640;
    let h = video.videoHeight || 480;
    if (w > maxDim || h > maxDim) {
      if (w > h) { h = Math.round(h * (maxDim / w)); w = maxDim; }
      else { w = Math.round(w * (maxDim / h)); h = maxDim; }
    }
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    
    // Jika kamera depan, balikkan secara horizontal agar natural
    if (facingMode === 'user') {
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, w, h);
    return canvas;
  };

  const takeSnapshot = (video) => {
    const canvas = createSnapshotCanvas(video);
    return canvas.toDataURL('image/jpeg', 0.80);
  };

  // Kalkulasi Nama Area Final
  const getFinalAreaString = () => {
    if (!selectedAreaId) return '';
    if (selectedAreaId === 'Lainnya') {
      return customOtherArea.trim() ? `Lainnya: ${customOtherArea.trim()}` : 'Lainnya';
    }
    return selectedAreaId;
  };

  const captureAndVerify = async () => {
    if (!videoRef.current) return;
    
    const finalArea = getFinalAreaString();
    if (!finalArea || (selectedAreaId === 'Lainnya' && !customOtherArea.trim())) {
      setStatus('Pilih atau tentukan area lokasi Anda!');
      return;
    }
    if (isLate && !lateReason.trim()) {
      setStatus('Harap isi alasan keterlambatan!');
      return;
    }

    // 1. Ambil snapshot instan & kunci (freeze) tampilan kamera agar tidak bergerak-gerak
    let base64Image = null;
    let snapshotCanvas = null;
    try {
      snapshotCanvas = createSnapshotCanvas(videoRef.current);
      base64Image = snapshotCanvas.toDataURL('image/jpeg', 0.85);
      setFrozenPhoto(base64Image);
      setIsFlashing(true);
      playShutterSound();
      setTimeout(() => setIsFlashing(false), 200);
    } catch (snapErr) {
      console.warn("Snapshot capture error", snapErr);
    }

    setIsDetecting(true);
    setStatus('📸 Foto terkunci! Menganalisis wajah & presensi...');

    // Simpan area terakhir di localStorage untuk kemudahan besok
    try {
      localStorage.setItem('satala_last_attendance_area', finalArea);
    } catch (e) {
      // ignore
    }

    try {
      let descriptor = null;
      let detection = null;
      let verificationResult = {
        isMatch: false,
        distance: null,
        noFaceDetected: false,
        hasReference: Boolean(referenceDescriptorStr)
      };

      // Helper timeout agar AI tidak pernah membuat aplikasi macet/muter terus
      const withTimeout = (promise, ms = 3000) => {
        return Promise.race([
          promise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('AI detection timeout')), ms))
        ]);
      };

      // 1. Pastikan model sudah termuat dengan batas waktu aman
      try {
        if (!areFaceApiModelsLoaded()) {
          await withTimeout(loadFaceApiModels(), 2500);
        }
      } catch (loadErr) {
        console.warn("Face model loading bypassed/timeout:", loadErr);
      }

      // 2. Deteksi wajah dari snapshotCanvas (Jauh lebih cepat & stabil di HP dibanding video stream)
      if (areFaceApiModelsLoaded() && snapshotCanvas) {
        try {
          const detectPromise = faceapi.detectSingleFace(
            snapshotCanvas,
            new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.12 })
          )
          .withFaceLandmarks()
          .withFaceDescriptor();

          detection = await withTimeout(detectPromise, 3000);
        } catch (errDet) {
          console.warn("Face detection canvas timeout/bypassed:", errDet);
        }
      }

      // 3. Fallback deteksi dari video jika canvas gagal
      if (!detection && areFaceApiModelsLoaded() && videoRef.current) {
        try {
          const detectPromise = faceapi.detectSingleFace(
            videoRef.current,
            new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.12 })
          )
          .withFaceLandmarks()
          .withFaceDescriptor();

          detection = await withTimeout(detectPromise, 2000);
        } catch (errVideo) {
          console.warn("Face detection video fallback bypassed:", errVideo);
        }
      }

      if (detection) {
        descriptor = detection.descriptor;

        if (referenceDescriptorStr) {
          try {
            const parsed = typeof referenceDescriptorStr === 'string' 
              ? JSON.parse(referenceDescriptorStr) 
              : referenceDescriptorStr;
            const referenceDescriptor = new Float32Array(parsed);
            const distance = faceapi.euclideanDistance(detection.descriptor, referenceDescriptor);
            verificationResult.distance = distance;
            verificationResult.isMatch = distance <= 0.60;
          } catch (e) {
            console.error("Error parsing reference descriptor", e);
          }
        }
      } else {
        verificationResult.noFaceDetected = true;
      }

      setStatus('✓ Foto berhasil diabadikan! Menyimpan kehadiran...');

      setTimeout(() => {
        onVerified(
          base64Image || (videoRef.current ? takeSnapshot(videoRef.current) : null),
          descriptor,
          finalArea,
          lateReason,
          verificationResult
        );
      }, 150);

    } catch (err) {
      console.error("Attendance capture error:", err);
      const fallbackImage = base64Image || (videoRef.current ? takeSnapshot(videoRef.current) : null);
      onVerified(fallbackImage, null, finalArea, lateReason, {
        isMatch: false,
        distance: null,
        noFaceDetected: true,
        hasReference: Boolean(referenceDescriptorStr)
      });
    }
  };

  // Filter area berdasarkan search query
  const filteredAreas = useMemo(() => {
    if (!searchAreaQuery.trim()) return ATTENDANCE_AREAS;
    const q = searchAreaQuery.toLowerCase().trim();
    return ATTENDANCE_AREAS.filter(area => 
      area.label.toLowerCase().includes(q) || 
      area.badge.toLowerCase().includes(q)
    );
  }, [searchAreaQuery]);

  const canSubmit = Boolean(
    selectedAreaId && 
    (selectedAreaId !== 'Lainnya' || customOtherArea.trim()) &&
    (!isLate || lateReason.trim())
  );

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        className="sm:max-w-md p-0 border border-amber-200/50 bg-white/95 backdrop-blur-md shadow-2xl rounded-3xl overflow-hidden pointer-events-auto flex flex-col max-h-[92dvh]"
        hideCloseButton={true}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Ambil Foto Area & Wajah</DialogTitle>
        <DialogDescription className="sr-only">Pindai wajah untuk absensi Satala Dermatech</DialogDescription>

        {/* Modal Header */}
        <div className="px-5 py-4 border-b border-amber-100/70 bg-gradient-to-r from-[#FDFBF7] via-white to-[#FDFBF7] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#8C6208] to-[#D4AF37] flex items-center justify-center shadow-xs text-white">
              <Camera className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-[#302621] leading-tight">Presensi Satala</h3>
              <p className="text-[11px] text-amber-900/60 font-medium">Verifikasi Lokasi & Wajah</p>
            </div>
          </div>
          
          <button 
            type="button" 
            onClick={onClose} 
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body (Scrollable) */}
        <div className="p-4 sm:p-5 flex flex-col overflow-y-auto space-y-4">

          {/* 1. SELEKSI AREA (SEARCHABLE DROPDOWN - SIMBOL BERSIH TANPA EMOJI) */}
          <div className="space-y-1.5 relative z-40" ref={dropdownRef}>
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-[#302621] flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#8C6208]" /> 
                Lokasi Keberadaan Anda *
              </label>

              {lastSavedArea && (
                <span className="text-[10px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full font-medium border border-amber-200/60">
                  Terakhir: {lastSavedArea.replace('Lainnya: ', '')}
                </span>
              )}
            </div>

            {/* Dropdown Trigger Button */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(prev => !prev)}
                className={`w-full flex items-center justify-between px-3.5 py-2.5 bg-white border rounded-xl text-xs transition-all shadow-xs ${
                  isDropdownOpen 
                    ? 'border-[#8C6208] ring-2 ring-[#8C6208]/20 bg-amber-50/20' 
                    : selectedAreaId 
                      ? 'border-amber-300 text-[#302621]' 
                      : 'border-slate-200 text-slate-400 hover:border-amber-300'
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  {(() => {
                    const currentObj = ATTENDANCE_AREAS.find(a => a.id === selectedAreaId);
                    if (!currentObj) {
                      return (
                        <>
                          <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                          <span className="text-slate-400">Pilih area keberadaan saat ini...</span>
                        </>
                      );
                    }
                    const CurrentIcon = currentObj.Icon;
                    return (
                      <>
                        <CurrentIcon className="w-4 h-4 text-[#8C6208] shrink-0" />
                        <span className="font-bold text-[#302621] truncate">{currentObj.label}</span>
                        <span className="text-[10px] text-slate-400 truncate">({currentObj.badge})</span>
                      </>
                    );
                  })()}
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0 ${isDropdownOpen ? 'rotate-180 text-[#8C6208]' : ''}`} />
              </button>

              {/* Dropdown Popover */}
              {isDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-amber-200/90 rounded-2xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                  {/* Search Input Box */}
                  <div className="p-2 border-b border-slate-100 bg-[#FDFBF7]">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        value={searchAreaQuery}
                        onChange={(e) => setSearchAreaQuery(e.target.value)}
                        placeholder="Ketik untuk mencari area..."
                        className="w-full pl-8 pr-7 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#8C6208] text-slate-800"
                        autoFocus
                      />
                      {searchAreaQuery && (
                        <button 
                          type="button" 
                          onClick={() => setSearchAreaQuery('')} 
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Options List with Clean Symbols */}
                  <div className="max-h-44 overflow-y-auto p-1 divide-y divide-slate-50">
                    {filteredAreas.length === 0 ? (
                      <div className="p-3 text-center text-xs text-slate-400">
                        Area tidak ditemukan
                      </div>
                    ) : (
                      filteredAreas.map((area) => {
                        const isSelected = selectedAreaId === area.id;
                        const AreaIcon = area.Icon;
                        return (
                          <button
                            key={area.id}
                            type="button"
                            onClick={() => {
                              setSelectedAreaId(area.id);
                              setIsDropdownOpen(false);
                              setSearchAreaQuery('');
                              if (area.id !== 'Lainnya') setCustomOtherArea('');
                            }}
                            className={`w-full flex items-center justify-between px-3 py-2 text-left rounded-lg text-xs transition-colors ${
                              isSelected 
                                ? 'bg-amber-50 text-[#8C6208] font-bold' 
                                : 'hover:bg-slate-50 text-slate-700'
                            }`}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <AreaIcon className={`w-4 h-4 shrink-0 ${isSelected ? 'text-[#8C6208]' : 'text-slate-500'}`} />
                              <div className="truncate">
                                <span className="font-semibold">{area.label}</span>
                                <span className="text-[10px] text-slate-400 ml-1.5 font-normal">({area.badge})</span>
                              </div>
                            </div>
                            {isSelected && (
                              <Check className="w-3.5 h-3.5 text-[#8C6208] shrink-0" />
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Input Tambahan Jika Memilih "Lainnya" */}
            {selectedAreaId === 'Lainnya' && (
              <div className="pt-2 mt-1 space-y-1 animate-in fade-in slide-in-from-top-1 duration-150">
                <label className="text-[11px] font-semibold text-amber-900 flex items-center gap-1">
                  <HelpCircle className="w-3 h-3 text-[#8C6208]" /> Sebutkan Nama Lokasi Spesifik *
                </label>
                <Input 
                  value={customOtherArea}
                  onChange={(e) => setCustomOtherArea(e.target.value)}
                  placeholder="Cth: Gudang Karton, Vendor Cetak, Bank Mandiri..."
                  className="h-8 text-xs bg-white border-amber-300 focus-visible:ring-[#8C6208]/30 rounded-lg"
                  autoFocus
                />
              </div>
            )}
          </div>

          {/* 2. ALASAN KETERLAMBATAN (JIKA TELAT) */}
          {isLate && (
            <div className="space-y-2 bg-rose-50/70 p-3.5 rounded-2xl border border-rose-200/80">
              <label className="text-xs font-bold text-rose-800 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-600" /> Anda Terlambat — Alasan *
                </span>
                <span className="text-[10px] font-normal text-rose-600">Wajib diisi</span>
              </label>

              {/* Quick Chips Alasan Telat */}
              <div className="flex flex-wrap gap-1">
                {LATE_REASON_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setLateReason(preset)}
                    className={`text-[10px] px-2 py-0.5 rounded-md border transition-all ${
                      lateReason === preset 
                        ? 'bg-rose-600 text-white border-rose-600 font-bold' 
                        : 'bg-white text-rose-700 border-rose-200 hover:bg-rose-100/50'
                    }`}
                  >
                    {preset}
                  </button>
                ))}
              </div>

              <Input 
                placeholder="Atau ketik alasan lain..." 
                value={lateReason}
                onChange={(e) => setLateReason(e.target.value)}
                className="h-8 text-xs bg-white border-rose-300 focus-visible:ring-rose-500 rounded-lg mt-1"
              />
            </div>
          )}

          {/* 3. KAMERA VIEWFINDER DENGAN GUIDE, LIVE CLOCK & SHUTTER FREEZE */}
          <div className="relative w-full aspect-[4/3] max-h-[38vh] bg-slate-950 rounded-2xl overflow-hidden shadow-inner shrink-0 group">
            {/* Live Video Stream */}
            <video 
              ref={videoRef}
              autoPlay 
              playsInline 
              muted 
              className={`w-full h-full object-cover ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
            />

            {/* Frozen Photo Snapshot (Diam / freeze seketika saat tombol memotret diklik) */}
            {frozenPhoto && (
              <img 
                src={frozenPhoto} 
                alt="Foto Presensi" 
                className="absolute inset-0 w-full h-full object-cover z-10 animate-in fade-in duration-150"
              />
            )}

            {/* Efek Kilatan Shutter Kamera (Flash Animation) */}
            <div 
              className={`absolute inset-0 bg-white z-40 transition-opacity duration-200 pointer-events-none ${
                isFlashing ? 'opacity-90' : 'opacity-0'
              }`} 
            />

            {/* Spinner saat loading AI / Capture */}
            {isDetecting && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 backdrop-blur-xs z-30 text-white space-y-2">
                <RefreshCw className="w-9 h-9 text-[#D4AF37] animate-spin" />
                <span className="text-xs font-bold tracking-wide">Menganalisis Presensi...</span>
              </div>
            )}

            {/* Top Bar Overlay: Jam Realtime & Tombol Balik Kamera */}
            <div className="absolute top-2.5 inset-x-2.5 flex items-center justify-between z-20 pointer-events-none">
              <div className="bg-black/65 backdrop-blur-md text-white px-2.5 py-1 rounded-full text-[10px] font-mono flex items-center gap-1.5 shadow-xs">
                <span className={`w-2 h-2 rounded-full ${frozenPhoto ? 'bg-blue-400' : faceDetectedLive ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`}></span>
                <span>{currentTime || 'Presensi Satala'}</span>
              </div>

              {/* Tombol Flip Camera (hanya tampil saat preview kamera aktif) */}
              {!frozenPhoto && (
                <button 
                  type="button" 
                  onClick={toggleFlipCamera}
                  title="Balik Kamera Depan / Belakang"
                  className="w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center pointer-events-auto backdrop-blur-md transition-transform active:scale-95 shadow-xs cursor-pointer"
                >
                  <SwitchCamera className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Guide Frame Oval (Warna berubah saat wajah terdeteksi, sembunyi saat foto terkunci) */}
            {!frozenPhoto && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className={`w-36 h-48 sm:w-40 sm:h-52 rounded-[50%] border-2 transition-all duration-300 ${
                  faceDetectedLive 
                    ? 'border-emerald-400/90 shadow-[0_0_25px_rgba(52,211,153,0.4)] scale-102' 
                    : 'border-white/40 border-dashed shadow-[0_0_15px_rgba(0,0,0,0.5)]'
                }`}>
                </div>
              </div>
            )}

            {/* Badge Bawah: Wajah Terdeteksi / Foto Terkunci */}
            <div className="absolute bottom-2.5 inset-x-0 flex justify-center pointer-events-none px-3 z-20">
              {frozenPhoto ? (
                <span className="bg-emerald-600/95 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1.5 shadow-md animate-in fade-in duration-150">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-200" /> Foto Terkunci & Diam
                </span>
              ) : faceDetectedLive ? (
                <span className="bg-emerald-600/90 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md animate-in fade-in duration-200">
                  <CheckCircle2 className="w-3 h-3 text-emerald-200" /> Wajah Terdeteksi
                </span>
              ) : (
                <span className="bg-black/65 backdrop-blur-md text-white/90 text-[10px] px-3 py-1 rounded-full shadow-xs">
                  Posisikan wajah di dalam lingkaran oval
                </span>
              )}
            </div>
          </div>

          {/* Status Guide Text */}
          <div className="text-center min-h-[20px] flex items-center justify-center">
            <p className={`text-xs font-medium transition-colors ${
              canSubmit ? 'text-emerald-700 font-semibold' : 'text-slate-500'
            }`}>
              {status}
            </p>
          </div>

          {/* Tombol Ambil Foto & Presensi */}
          <Button 
            onClick={captureAndVerify} 
            disabled={isDetecting || !canSubmit}
            className={`w-full h-12 text-white font-bold rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 ${
              canSubmit && !isDetecting 
                ? 'bg-gradient-to-r from-[#8C6208] to-[#B58B22] hover:from-[#6E4C06] hover:to-[#8C6208] cursor-pointer active:scale-[0.98] shadow-[#8C6208]/20' 
                : 'bg-slate-300 cursor-not-allowed opacity-80'
            }`}
          >
            {isDetecting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-white" />
                <span>Menyimpan Presensi...</span>
              </>
            ) : (
              <>
                <Camera className="w-4 h-4 text-white" />
                <span>Ambil Foto & Absen Sekarang</span>
              </>
            )}
          </Button>

        </div>
      </DialogContent>
    </Dialog>
  );
}
